/* eslint-disable react/require-default-props */
'use client';

import Icon from '@mdi/react';
import { Tooltip, TooltipProps } from '@mui/material';
import { motion, MotionProps } from 'framer-motion';
import { forwardRef } from 'react';

import styles from './IconButton.module.sass';

interface IconButtonProps {
    label: string;
    icon: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    isMini?: boolean;
    variant?: IconButtonVariant;
    enabled?: boolean;
    tooltipPlacement?: TooltipProps['placement'];
    className?: string;
    style?: MotionProps['style'];
    badgeValue?: number | string;
    children?: string | React.ReactNode;
}

export enum IconButtonVariant {
    Regular = 'regular',
    Primary = 'primary',
    Accent = 'accent',
    Success = 'success',
    Danger = 'danger',
    Transparent = 'transparent',
}

/* eslint-disable react/display-name */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (
        {
            label,
            icon,
            onClick,
            isMini = false,
            variant = IconButtonVariant.Regular,
            enabled = true,
            tooltipPlacement = 'top',
            className = '',
            style = {},
            badgeValue,
            children = undefined,
        },
        ref,
    ) => {
        const sizes = {
            mini: '1.5rem',
            normal: '3rem',
        };

        const size = isMini ? sizes.mini : sizes.normal;
        const canEnable = enabled && onClick !== undefined;

        const variantClassName =
            variant == IconButtonVariant.Regular
                ? ''
                : styles[`iconbutton--${variant}`];

        return (
            <Tooltip
                title={label}
                placement={tooltipPlacement}
                disableHoverListener={!canEnable}
            >
                <motion.button
                    ref={ref ?? undefined}
                    aria-label={label}
                    tabIndex={canEnable ? 0 : -1}
                    style={style}
                    className={`
                        ${styles.iconbutton}
                        ${variantClassName}
                        ${isMini ? styles['iconbutton--mini'] : ''}
                        ${children ? styles['iconbutton--children'] : ''}
                        rounded-full ${className}`}
                    onClick={canEnable ? onClick : undefined}
                    initial={{
                        scale: 0.5,
                        opacity: 0,
                        minHeight: size,
                        minWidth: size,
                    }}
                    animate={{
                        scale: 1,
                        opacity: canEnable ? 1 : 0.5,
                        minHeight: size,
                        minWidth: size,
                    }}
                    whileHover={canEnable ? { scale: 1.05 } : {}}
                    whileTap={canEnable ? { scale: 0.95 } : {}}
                    transition={{ duration: 0.1 }}
                >
                    <Icon path={icon} size={isMini ? 0.7 : 1} />
                    {children}
                    {badgeValue !== undefined && (
                        <motion.span
                            className={styles['iconbutton__badge']}
                            initial={{
                                scale: 0,
                            }}
                            animate={{
                                scale: 1,
                            }}
                            transition={{
                                delay: 1,
                            }}
                        >
                            {badgeValue}
                        </motion.span>
                    )}
                </motion.button>
            </Tooltip>
        );
    },
);

export default IconButton;
